import { getAmplitudeClient } from '@/features/amplitude';
import {
  useHistory,
  useJobOpeningIdOrNull,
  useWorkspaceId,
} from '@/hooks/router';
import { usePostHog } from 'posthog-js/react';
import React, { createContext, useCallback, useState } from 'react';
import { OnboardingStepType } from '../types/onboarding-steps';
import { ONBOARDING_STEPS } from '../utils/onboarding-steps';

export const OnboardingModalContext = createContext<{
  type: OnboardingStepType | null;
  step: number | null;
  skipOnboarding: (type: OnboardingStepType) => void;
  nextStep: (type: OnboardingStepType) => void;
  startOnboarding: () => void;
}>({
  type: null,
  step: null,
  skipOnboarding: () => {},
  nextStep: () => {},
  startOnboarding: () => {},
});

export function OnboardingContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const workspaceId = useWorkspaceId();
  const jobOpeningId = useJobOpeningIdOrNull();
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();
  const history = useHistory();
  const [onboardingData, setOnboardingData] = useState<{
    type?: OnboardingStepType | null;
    step?: number | null;
  }>({
    type: null,
    step: null,
  });

  const startOnboarding = useCallback(() => {
    setOnboardingData({
      type: 'applied',
      step: 1,
    });
  }, [setOnboardingData]);

  const skipOnboarding = useCallback(
    (type: OnboardingStepType) => {
      setOnboardingData({
        type: null,
        step: null,
      });

      amplitude?.logEvent(`onboarding/skip/${type}`, {
        'workspace id': workspaceId,
        'job opening id': jobOpeningId,
      });
      posthog?.capture(`hire_admin_app:onboarding/skip/${type}`, {
        'workspace id': workspaceId,
        'job opening id': jobOpeningId,
      });

      if (jobOpeningId) {
        history.replace(
          `/admin/${workspaceId}/openings/${jobOpeningId}/pipeline`
        );
      }
    },
    [setOnboardingData, workspaceId, jobOpeningId, history, amplitude, posthog]
  );

  const nextStep = useCallback(
    (type: OnboardingStepType) => {
      const currentStep = ONBOARDING_STEPS[type];
      const currentStepNumber = currentStep?.step || 1;

      // Find the next step in the sequence
      const nextStepNumber = currentStepNumber + 1;
      const nextStepType = Object.values(ONBOARDING_STEPS).find(
        (step) => step.step === nextStepNumber
      )?.id;

      if (nextStepType) {
        setOnboardingData({
          type: nextStepType,
          step: nextStepNumber,
        });
      } else {
        // If no next step, finish onboarding
        setOnboardingData({
          type: null,
          step: null,
        });

        history.replace(
          `/admin/${workspaceId}/openings/${jobOpeningId}/pipeline`
        );

        amplitude?.logEvent(`onboarding/${type}`, {
          'workspace id': workspaceId,
          'job opening id': jobOpeningId,
        });
        posthog?.capture(`hire_admin_app:onboarding/${type}`, {
          'workspace id': workspaceId,
          'job opening id': jobOpeningId,
        });
      }
    },
    [setOnboardingData, workspaceId, jobOpeningId, history, amplitude, posthog]
  );

  return (
    <OnboardingModalContext.Provider
      value={{
        type: onboardingData.type ?? null,
        step: onboardingData.step ?? null,
        skipOnboarding,
        nextStep,
        startOnboarding,
      }}
    >
      {children}
    </OnboardingModalContext.Provider>
  );
}
